<script>
import childNameOrChildren from '@/mixins/childNameOrChildren';
import {hasEditPermission, currentRoleLevelName} from '@/mixins/roleLevelPermissions';
import { mapState } from 'vuex';

export default {
	mixins: [childNameOrChildren],
	props: ['agreementData'],
	data() {
		return { 
			isViewMode : !hasEditPermission('section-parenting'),
			showLearnButton: ['client-level-1', 'client-level-2'].includes(currentRoleLevelName)
		};	
	},
	computed: {
		...mapState(['currentRoleLevel']),
		parenting() {
			return this.agreementData.parenting;
			// return this.agreementData.parenting =  this.agreementData.parenting.hasOwnProperty('additionalClausesParentingAgreementsToRecitals') 
			// 	? this.agreementData.parenting 
			// 	: {...this.agreementData.parenting, additionalClausesParentingAgreementsToRecitals: []};
		},
		isUserSeparatingParty(){
			return this.currentRoleLevel.roleAssociated === 'party'
		},
		dynamicQuestions(){
			if(this.isUserSeparatingParty){
				return {
					nestingArrangement: "Will you use a Nesting Arrangement for a period of time?",
					nestingResidence: "Where will each parent reside when it is not their parenting time?",
					facilitatedCommunication: `Will you faciliate communication between the parents and ${this.childNameOrChildren} during parenting time?`,
					nestingSecondResidencePlan: "How will you share the cost of the Shared Second Residence?"
				}
			}
			else{
				return {
					nestingArrangement: `Are the parents intending to exercise parenting time in a nesting arrangement where ${this.childNameOrChildren} will remain in the Family Residence and the parents will come and go according to the Parenting Time Schedule?`,
					nestingResidence: "Where will the parties reside when it is not their Parenting Time?",
					facilitatedCommunication: `Will the parties facilitate communication between ${this.childNameOrChildren} and the other party during parenting time?`,
					nestingSecondResidencePlan: "How will the parties share the cost of the Shared Second Residence?"
				}
			}
		},
		nestingResidenceOptions(){
            return [
				{
					text: this.isUserSeparatingParty ? 'Each parent will maintain their own separate residence': 'Each will maintain their own separate residence',
					value:'Each will maintain their own separate residence',
				},
				{
					text: this.isUserSeparatingParty ?  'Both parents will share a second residence' : 'The parties will share a second residence',
					value: 'The parties will share a second residence'
				}
				
			]
		},
		nestingSecondResidencePlanOptions(){
			return[
				{
					text: this.isUserSeparatingParty ? 'Equally between the parents' : 'Equally between them',
					value: 'equally between them',
				},
				{
					text: this.isUserSeparatingParty ?'As agreed between the parents' : 'As agreed between them',
					value: 'as agreed between them',
				},
				{
					text:  this.isUserSeparatingParty ? 'In proportion to your incomes as calculated under the Federal Child Support Guidelines': 'In proportion to their incomes as calculated under the Federal Child Support Guidelines',
					value: 'in proportion to their incomes as calculated under the Federal Child Support Guidelines',
				},
			]
		}
	},
	mounted(){
		this.agreementData.parenting =  this.agreementData.parenting.hasOwnProperty('additionalClausesParentingAgreementsToRecitals') 
			? this.agreementData.parenting 
			: {...this.agreementData.parenting, additionalClausesParentingAgreementsToRecitals: []};
	}
};
</script>

<template>
	<b-card>
		<div slot="header" class="section-heading">
			<h4>Parenting Agreements</h4>
			<a href="#" onclick="Intercom('showArticle', 11106614)" v-if="this.showLearnButton">Learn &nbsp;<b-icon-book/></a>
		</div>

		<yes-no-choice
			v-model="parenting.nestingArrangement"
			class="mb-3"
			:label="dynamicQuestions.nestingArrangement"
			reset-undefined
			:testid="'parenting-agreement-child-communication'"
			:disabled="isViewMode"
			toolTipText="A nesting arrangement is when the child remains in the family home and the parents live in the family home during their parenting time according to their Parenting Time Schedule.
			Each parent will arrange for another place to reside when it is not their parenting time or will share a second residence.
			This is not done often but can sometimes be used temporarily while the family transitions to a more long-term solution. "
		/>

		<conditional-group
			class="my-3"
			:condition="parenting.nestingArrangement"
		>
			<radio-group
				v-model="parenting.nestingResidence"
				select
				class="mt-3"
				reset-undefined
				input-cols="8"
				:label="dynamicQuestions.nestingResidence"
				:options="nestingResidenceOptions"
				:testid="'parenting-agreement-reside-not-parenting-time'"
				:length="'long'"
				:disabled="isViewMode"
			/>

			<conditional-group
				class="my-3"
				:condition="
					parenting.nestingResidence ===
					'The parties will share a second residence'
				"
			>
				<radio-group
					v-model="parenting.nestingSecondResidencePlan"
					class="mb-3"
					reset-undefined
					input-cols="8"
					:label="dynamicQuestions.nestingSecondResidencePlan"
					select
					:options="nestingSecondResidencePlanOptions"
					:testid="'parenting-agreement-shared-second-residence-cost-share'"
					:length="'long'"
					:disabled="isViewMode"
				/>
			</conditional-group>

			<check-group
				v-model="parenting.nestingSecondResidenceUpkeep"
				reset-undefined
				class="mt-3"
				label="What expectations of shared household upkeep would you like to include?"
				other
				:options="[
					{
						text: 'Leaving the kitchen clean, organized, and with clean dishes',
						value: 'leaving the kitchen clean, organized, and with clean dishes',
					}, // value fix
					{
						text: `Removing food from the refrigerator that is not for ${childNameOrChildren}`,
						value: `removing food from the refrigerator that is not for ${childNameOrChildren}`,
					},
					{
						text: 'Cleaning the bathroom for the other parent’s use',
						value: 'cleaning the bathroom for the other parent’s use',
					},
					{
						text: 'Removing the sheets from the bed in the master bedroom',
						value: 'removing the sheets from the bed in the master bedroom',
					},
				]"
				:testid="'parenting-agreement-nesting-second-residence-up-keep'"
				:id="'parenting-agreement-nesting-second-residence-up-keep'"
				:disabled="isViewMode"
				toolTipText='If you choose this arrangement, it is wise to outline clear expectations.
				Often matters of privacy and cleanliness cause conflict.  If you have a timeline on this arrangement make a note and adjust in the editor to specify in the clause,
				"Until otherwise agreed by the parties, ..." or "Until September 1st, 20xx ..."'
			/>
		</conditional-group>

		<yes-no-choice
			v-model="parenting.facilitatedCommunication"
			class="mb-3"
			:label="dynamicQuestions.facilitatedCommunication"
			reset-undefined
			:testid="'parenting-agreement-facilitate-communication'"
			:disabled="isViewMode"
			toolTipText="If your child is requires regular communication with each of you when it is not your parenting time,
			you can agree to phone calls or video calls during parenting time to maintain connection.  This can be very disruptive
			to parenting time, though, and if you choose to specify this, we've provided common options that allow for some variability in time.
			If you want something specific, specify a time that will consistently be manageable. "
		/>

		<conditional-group
			class="my-3"
			:condition="parenting.facilitatedCommunication"
		>
			<check-group
				v-model="parenting.facilitatedCommunicationMethods"
				class="mb-3"
				reset-undefined
				:options="[
					{ text: 'Telephone', value: 'telephone calls' }, // value fix
					{ text: 'Text message', value: 'text messages' },
					{ text: 'Video call', value: 'video calls' },
				]"
				label="By what method of communication?"
				other
				:testid="'parenting-agreement-method-of-communication'"
				:id="'parenting-agreement-method-of-communication'"
				:disabled="isViewMode"
			/>

			<radio-group
				v-model="parenting.facilitatedCommunicationWhen"
				reset-undefined
				:options="[
					{
						text: `At times determined by ${childNameOrChildren}`, // value fixs very hard
						value: `at times determined by ${childNameOrChildren}`,
					},
					{
						text: 'When the other parent makes contact',
						value: 'when the other parent makes contact',
					},
					{
						text: isUserSeparatingParty ? 'As agreed between the parents' :  'As agreed between the parties',
						value: 'as agreed between the parties',
					},
				]"
				input-cols="6"
				select
				label="When will this communication occur?"
				other
				:testid="'parenting-agreement-facilitated-communication-when'"
				:length="'long'"
				:disabled="isViewMode"
			/>
		</conditional-group>
		<additional-clauses
			:title="'Additional Clauses into Recitals'" :newLabel="'Add Clause to Recitals'" class="mt-2"
			v-model="parenting.additionalClausesParentingAgreementsToRecitals" :testid="'parenting-agreement-add-clause-to-recitals-'"
			:disabled="isViewMode"
		/>
		<additional-clauses
			:title="'Additional Clauses into Sections'" :newLabel="'Add Clause to Section'" class="mt-2"
			v-model="parenting.additionalClausesParentingAgreements" :testid="'parenting-agreement-add-clause-'"
			:disabled="isViewMode"
		/>
	</b-card>
</template>
